import React, { useEffect, useState } from "react";
import { useApparelShop } from "@/hooks/useApparelShop";
import { ApparelProduct } from "@/types";
import ProductDetailsDialog from "@/components/apparel/shop/ProductDetailsDialog";
import CartSheet from "@/components/apparel/shop/CartSheet";
import CheckoutSheet from "@/components/apparel/shop/CheckoutDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import ParentLayoutUnified from "@/components/layout/ParentLayoutUnified";
import ParentCard from "@/components/parent-portal/ParentCard";
import ParentLoadingState from "@/components/parent-portal/ParentLoadingState";
import ParentEmptyState from "@/components/parent-portal/ParentEmptyState";
import { useResponsive } from "@/hooks/useResponsive";
import { RESPONSIVE_SPACING, RESPONSIVE_TYPOGRAPHY, TOUCH_TARGETS } from "@/design-tokens/responsive";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ShopHeader } from "@/components/apparel/shop/layout/ShopHeader";
import { ShopLoadingState } from "@/components/apparel/shop/layout/ShopLoadingState";
import { ShopErrorState } from "@/components/apparel/shop/layout/ShopErrorState";
import { ProductGrid } from "@/components/apparel/shop/layout/ProductGrid";
import { PaymentService } from '@/services/payment';
import { supabase } from '@/lib/supabase';
import { useParentDashboard } from '@/hooks/useParentDashboard';

const ParentPortalApparel = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isMobile } = useResponsive();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [filteredProducts, setFilteredProducts] = useState<ApparelProduct[]>([]);
  
  // Get parent's students data for checkout
  const { students: parentStudents } = useParentDashboard();

  const isParentPortal = true;

  const {
    products,
    loading,
    error,
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateSize,
    clearCart,
    selectedProduct,
    detailsOpen,
    setDetailsOpen,
    cartOpen,
    setCartOpen,
    checkoutOpen,
    setCheckoutOpen,
    handleProductSelect,
    cartItemCount,
    cartSubtotal
  } = useApparelShop(isParentPortal);

  // DEBUG: Log cart state
  console.log('ParentPortalApparel - cart:', cart);
  console.log('ParentPortalApparel - cartItemCount:', cartItemCount);
  console.log('ParentPortalApparel - cart length:', cart?.length);
  console.log('ParentPortalApparel - cart items:', cart.map(item => ({ product: item.product.name, quantity: item.quantity })));
  console.log('ParentPortalApparel - cartOpen:', cartOpen);
  console.log('ParentPortalApparel - checkoutOpen:', checkoutOpen);
  console.log('ParentPortalApparel - parentStudents:', parentStudents);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);


  useEffect(() => {
    if (products) {
      let filtered = [...products];
      
      if (activeCategory !== "all") {
        filtered = filtered.filter(product => 
          product.category?.toLowerCase() === activeCategory.toLowerCase()
        );
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(product => 
          product.name.toLowerCase().includes(query) || 
          product.description?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query)
        );
      }
      
      setFilteredProducts(filtered);
    }
  }, [products, activeCategory, searchQuery]);

  const handleCheckout = () => {
    console.log('ParentPortalApparel - handleCheckout called!');
    console.log('ParentPortalApparel - cartItemCount:', cartItemCount);
    console.log('ParentPortalApparel - cart:', cart);
    
    if (cartItemCount === 0) {
      toast.error("Your cart is empty");
      return;
    }
    
    console.log('ParentPortalApparel - Closing cart, opening checkout');
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const handleCheckoutWithMetadata = async (metadata: any) => {
    if (cartItemCount === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (!user?.email) {
      toast.error("Please log in to checkout");
      return;
    }
    const organizationId = user.user_metadata?.organization_id;
    // Check if Stripe is connected for this org
    if (organizationId) {
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('billing_settings')
        .eq('id', organizationId)
        .single();
      let stripeConnected = false;
      if (!orgError && orgData?.billing_settings) {
        const billing = typeof orgData.billing_settings === 'string' ? JSON.parse(orgData.billing_settings) : orgData.billing_settings;
        stripeConnected = !!billing?.stripe_account_id;
      }
      if (!stripeConnected) {
        toast.error('Online payment is not available for this organization. Please contact the activity center for payment options.');
        return;
      }
    }
    try {
      const toastId = toast.loading("Preparing checkout...");
      const totalAmount = cartSubtotal();
      const result = await PaymentService.createOneTimePayment({
        amount_cents: totalAmount,
        currency: 'usd',
        name: 'Apparel Purchase',
        description: `${cartItemCount} item(s) from apparel shop`,
        customer_email: user.email,
        success_url: `${window.location.origin}/payment-success?source=apparel`,
        cancel_url: `${window.location.origin}/payment-canceled?source=apparel`,
        organization_id: organizationId,
        isPlatformPayment: false,
        metadata: {
          user_id: user.id,
          organization_id: organizationId || '',
          cart_items: JSON.stringify(cart.map(item => ({
            product_id: item.product.id,
            quantity: item.quantity,
            size: item.size
          }))),
          ...metadata,
        }
      });
      toast.dismiss(toastId);
      if (result?.url) {
        toast.success("Redirecting to secure checkout");
        clearCart();
        setCheckoutOpen(false);
        setTimeout(() => {
          window.location.href = result.url;
        }, 500);
      } else if (result?.error) {
        throw new Error(`Payment service error: ${result.error}`);
      } else {
        throw new Error("Couldn't create checkout session - no URL returned");
      }
    } catch (error) {
      toast.dismiss();
      const errorMessage = error instanceof Error ? error.message : "Please try again or contact support";
      toast.error("Checkout failed", { description: errorMessage });
      console.error("Checkout error:", error);
    }
  };

  const getUniqueCategories = () => {
    if (!products) return [];
    const categories = products.map(p => p.category || "Uncategorized");
    return ["all", ...new Set(categories)];
  };

  if (error) {
    return (
      <ParentLayoutUnified
        pageTitle=""
        pageDescription=""
      >
        <ShopErrorState error={error} />
      </ParentLayoutUnified>
    );
  }

  if (loading) {
    return (
      <ParentLayoutUnified
        pageTitle=""
        pageDescription=""
      >
        <ParentLoadingState message="Loading apparel shop..." />
      </ParentLayoutUnified>
    );
  }

  return (
    <ParentLayoutUnified
      pageTitle=""
      pageDescription=""
      rightContent={
        <div className={cn(
          "flex items-center gap-3",
          isMobile && "flex-col w-full"
        )}>
          <div className={cn(
            "relative",
            isMobile ? "w-full" : "w-64"
          )}>
            <Input
              placeholder="Search apparel..."
              className={cn(
                isMobile && "text-base" // Prevent zoom on iOS
              )}
              style={{ minHeight: TOUCH_TARGETS.minSize }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <ShopHeader 
            key={`cart-${cartItemCount}`}
            cartItemCount={cartItemCount}
            onCartOpen={() => setCartOpen(true)}
          />
        </div>
      }
    >
      <div className={cn(
        RESPONSIVE_SPACING.container.mobile
      )}>
        <ParentCard>
          <motion.div
            initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Tabs defaultValue="all" value={activeCategory} onValueChange={setActiveCategory}>
              <TabsList className={cn(
                "mb-6 bg-gradient-to-r from-slate-50 via-white to-slate-50 p-1.5 rounded-2xl shadow-lg border border-slate-200/60 backdrop-blur-sm overflow-x-auto flex-nowrap whitespace-nowrap max-w-full overflow-y-hidden",
                isMobile && "gap-2"
              )}>
              {getUniqueCategories().map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  className={cn(
                    "capitalize rounded-xl font-medium transition-all duration-300 text-slate-600 hover:text-slate-800 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-blue-500/25 data-[state=active]:scale-105 hover:bg-slate-50 relative",
                    isMobile ? "px-4 py-2 text-sm" : "px-6 py-2.5 text-sm",
                    RESPONSIVE_TYPOGRAPHY.bodySmall
                  )}
                  style={{ minHeight: TOUCH_TARGETS.minSize }}
                >
                  <span className="relative z-10">{category === "all" ? "All Items" : category}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={activeCategory} className="mt-0">
              {loading ? (
                <ShopLoadingState />
              ) : filteredProducts.length > 0 ? (
                <ProductGrid 
                  products={filteredProducts}
                  onProductSelect={handleProductSelect}
                />
              ) : (
                <ParentEmptyState
                  icon={ShoppingBag}
                  title="No products found"
                  description="Try adjusting your search or filter to find what you're looking for."
                />
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </ParentCard>
    </div>

      {selectedProduct && (
        <ProductDetailsDialog
          product={selectedProduct}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          onAddToCart={addToCart}
        />
      )}

      <CartSheet 
        items={cart}
        open={cartOpen} 
        onOpenChange={setCartOpen}
        onRemoveItem={removeFromCart}
        onUpdateQuantity={updateQuantity}
        onUpdateSize={updateSize}
        onCheckout={handleCheckout}
        cartItems={cart}
        cartSubtotal={cartSubtotal}
      />

      {checkoutOpen && (
        <CheckoutSheet
          open={checkoutOpen}
          onOpenChange={setCheckoutOpen}
          cartItems={cart}
          families={[]}
          students={parentStudents || []}
          onCheckout={handleCheckoutWithMetadata}
          onRemoveProduct={removeFromCart}
          onUpdateQuantity={updateQuantity}
          onUpdateSize={updateSize}
        />
      )}
    </ParentLayoutUnified>
  );
};

export default ParentPortalApparel;
